// generatePhotoPdf.js
// Photo reports, built from the Report Editor slots. Electrical and structural
// share the same page: letter portrait, 2 × 2 grid, bold captions, address
// header, firm logo footer and page numbers. Only the cell list and the header
// title differ.
//
// Electrical:
//
// Letter, portrait, one continuous 2 × 2 grid (four photos per page):
//   PHOTO-01, PHOTO-02, then one photo per form item in form order
//   (N/A items print a NOT APPLICABLE panel; thermography, when performed,
//   prints a "Refer to separate thermography report" panel), then the repair
//   photos, numbered on from the last item: "PHOTO-29 REPAIR ITEM 7".
// Captions are bold, below each photo. No findings boxes — repair text goes on
// the jurisdiction form.
//
// Header: project address, then "Electrical Recertification — Photographs".
// Footer: firm logo (or firm name) centered, "Page X of Y" at right.

import jsPDF from 'jspdf';
import { getReportItems, LEAD_ITEMS, numberRepairPhotos, repairCaption } from '../reportItems';
import { isNotApplicable } from '../reportSlots';
import { numberStructuralPhotos } from '../structuralItems';

// ─── Page geometry (points; 72 pt = 1 in) ─────────────────────────────────────

const PAGE_W      = 612;
const PAGE_H      = 792;
const MARGIN_X    = 54;              // 0.75 in
const TOP         = 30;
const HEADER_H    = 34;
const FOOTER_H    = 52;
const CONTENT_W   = PAGE_W - MARGIN_X * 2;
const BODY_TOP    = TOP + HEADER_H + 8;
const BODY_BOTTOM = PAGE_H - FOOTER_H;

const COL_GAP     = 24;
const CELL_W      = (CONTENT_W - COL_GAP) / 2;     // 240 pt ≈ 3.33 in
const CAPTION_H   = 18;
const ROW_GAP     = 22;
const GRID_IMG_H  = Math.floor((BODY_BOTTOM - BODY_TOP - CAPTION_H * 2 - ROW_GAP) / 2);  // ≈ 4.2 in

const MAX_IMG_PX  = 1200;            // longest side after downscaling (file size)
const JPEG_Q      = 0.72;

// ─── Image loading ────────────────────────────────────────────────────────────

const loadImage = (url) => new Promise((resolve, reject) => {
  if (!url) { reject(new Error('No URL')); return; }
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      const scale  = Math.min(1, MAX_IMG_PX / Math.max(img.naturalWidth, img.naturalHeight));
      const canvas = document.createElement('canvas');
      canvas.width  = Math.round(img.naturalWidth * scale);
      canvas.height = Math.round(img.naturalHeight * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve({ dataUrl: canvas.toDataURL('image/jpeg', JPEG_Q), width: canvas.width, height: canvas.height });
    } catch (e) {
      reject(e);
    }
  };
  img.onerror = () => reject(new Error(`Image failed to load: ${url.split('?')[0]}`));
  // Safari reuses the copy the page already showed (loaded without CORS) and then
  // refuses to draw it; a unique query parameter forces a fresh CORS request.
  img.src = `${url}${url.includes('?') ? '&' : '?'}pdf=${Date.now()}`;
});

// Full image, then the thumbnail if the full image will not load
const loadPhoto = async (p) => {
  const urls = [p?.fullUrl, p?.url, p?.thumbUrl].filter((u, i, a) => u && a.indexOf(u) === i);
  let last = new Error('No URL');
  for (const u of urls) {
    try { return await loadImage(u); } catch (e) { last = e; }
  }
  throw last;
};


// ─── Build the ordered list of cells ──────────────────────────────────────────

const buildCells = (county, slots, photosById) => {
  const cells = [];
  const repairs = [];
  const repairLabels = numberRepairPhotos(county, slots);

  LEAD_ITEMS.forEach(it => {
    const slot = slots?.[it.key];
    cells.push({ caption: `${it.photoLabel} ${it.title}`, photo: photosById[slot?.photos?.[0]?.photoId] || null });
  });

  getReportItems(county).forEach(it => {
    const slot = slots?.[it.key];
    if (!slot) return;
    const caption = `${it.photoLabel} ${it.title}`;

    if (isNotApplicable(slot)) {
      cells.push({ caption, panelText: 'NOT APPLICABLE' });
      return;
    }
    if (it.noPhoto) {
      cells.push({ caption, panelText: 'REFER TO SEPARATE THERMOGRAPHY REPORT' });
      return;
    }

    cells.push({ caption, photo: photosById[slot.photos?.[0]?.photoId] || null });
    (slot.photos || []).slice(1).forEach((p, i) => {
      repairs.push({ caption: `${repairLabels[it.key][i]} ${repairCaption(it)}`, photo: photosById[p.photoId] || null });
    });
  });

  return [...cells, ...repairs];
};

// ─── Main ─────────────────────────────────────────────────────────────────────

// Structural: PHOTO-01 aerial, elevations, each section in form order (N/A
// sections print a NOT APPLICABLE panel), then repair photos "REPAIR ITEM 7a".
const buildStructuralCells = (county, slots, siteSystems, photosById) =>
  numberStructuralPhotos(county, slots, siteSystems).map(c => (
    c.na
      ? { caption: `${c.label} ${c.caption}`, panelText: 'NOT APPLICABLE' }
      : { caption: `${c.label} ${c.caption}`, photo: photosById[c.photoId] || null }
  ));

const byId = (photos) => {
  const map = {};
  (photos || []).forEach(p => { map[p.id] = p; });
  return map;
};

export const generatePhotoPdf = async ({ project, userProfile, slots, photos }) => {
  const photosById = byId(photos);
  return renderPhotoGrid({
    project, userProfile,
    title: 'Electrical Recertification — Photographs',
    cells: buildCells(project?.county, slots, photosById),
  });
};

export const generateStructuralPhotoPdf = async ({ project, userProfile, slots, siteSystems, photos }) => {
  const photosById = byId(photos);
  return renderPhotoGrid({
    project, userProfile,
    title: 'Structural Recertification — Photographs',
    cells: buildStructuralCells(project?.county, slots, siteSystems, photosById),
  });
};

// ─── Shared page renderer ─────────────────────────────────────────────────────

const renderPhotoGrid = async ({ project, userProfile, title, cells }) => {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });

  const address = [project?.address, project?.city, project?.state, project?.zip].filter(Boolean).join(', ');

  let logo = null;
  if (userProfile?.logoUrl) {
    try { logo = await loadImage(userProfile.logoUrl); } catch (e) { console.warn('Logo not loaded:', e); }
  }

  // ─── Page furniture ─────────────────────────────────────────────────────

  const drawHeader = () => {
    pdf.setTextColor(0, 0, 0);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.text(address, PAGE_W / 2, TOP + 11, { align: 'center' });
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.text(title, PAGE_W / 2, TOP + 25, { align: 'center' });
  };

  const drawFooter = (pageNum, pageCount) => {
    const baseY = PAGE_H - 18;
    if (logo) {
      const ratio = Math.min(150 / logo.width, 36 / logo.height);
      const lw = logo.width * ratio;
      const lh = logo.height * ratio;
      pdf.addImage(logo.dataUrl, 'JPEG', (PAGE_W - lw) / 2, baseY - lh, lw, lh);
    } else if (userProfile?.firmName) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(60, 60, 60);
      pdf.text(userProfile.firmName, PAGE_W / 2, baseY, { align: 'center' });
    }
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(0, 0, 0);
    pdf.text(`Page ${pageNum} of ${pageCount}`, PAGE_W - MARGIN_X, baseY, { align: 'right' });
  };

  let pageStarted = false;
  const newPage = () => {
    if (pageStarted) pdf.addPage();
    pageStarted = true;
    drawHeader();
  };

  // ─── Cell pieces ────────────────────────────────────────────────────────

  const drawPanel = (x, y, w, h, text) => {
    pdf.setDrawColor(156, 163, 175);
    pdf.setLineWidth(0.75);
    pdf.setFillColor(236, 238, 241);
    pdf.rect(x, y, w, h, 'FD');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.setTextColor(55, 65, 81);
    const lines = pdf.splitTextToSize(text, w - 28);
    const lineH = 18;
    const startY = y + h / 2 - ((lines.length - 1) * lineH) / 2 + 5;
    lines.forEach((ln, i) => pdf.text(ln, x + w / 2, startY + i * lineH, { align: 'center' }));
    pdf.setTextColor(0, 0, 0);
  };

  // Photo scaled to fit (x, y, w, h) without cropping, centered in the frame;
  // placeholder if it cannot load
  const drawPhoto = async (photo, x, y, w, h, label) => {
    if (photo) {
      try {
        const img = await loadPhoto(photo);
        const ratio = Math.min(w / img.width, h / img.height);
        const dw = img.width * ratio;
        const dh = img.height * ratio;
        pdf.addImage(img.dataUrl, 'JPEG', x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
        return;
      } catch (e) {
        console.warn('Photo not loaded:', label, e);
      }
    }
    pdf.setDrawColor(156, 163, 175);
    pdf.setLineWidth(0.75);
    pdf.rect(x, y, w, h);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(75, 85, 99);
    pdf.text(photo ? 'Image unavailable' : 'No photo selected', x + w / 2, y + h / 2, { align: 'center' });
    pdf.setTextColor(0, 0, 0);
  };

  const drawCaption = (text, x, y) => {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    pdf.text(text, x, y + 12, { maxWidth: CELL_W });
  };

  const drawCell = async (cell, x, y, imgH) => {
    if (cell.panelText) drawPanel(x, y, CELL_W, imgH, cell.panelText);
    else await drawPhoto(cell.photo, x, y, CELL_W, imgH, cell.caption);
    drawCaption(cell.caption, x, y + imgH);
  };

  // ─── One continuous 2 × 2 grid ──────────────────────────────────────────

  const colX = [MARGIN_X, MARGIN_X + CELL_W + COL_GAP];
  const rowY = [BODY_TOP, BODY_TOP + GRID_IMG_H + CAPTION_H + ROW_GAP];

  for (let i = 0; i < cells.length; i++) {
    const pos = i % 4;
    if (pos === 0) newPage();
    await drawCell(cells[i], colX[pos % 2], rowY[Math.floor(pos / 2)], GRID_IMG_H);
  }

  if (!pageStarted) newPage();

  // Footer with page numbers once the total is known
  const pageCount = pdf.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    drawFooter(i, pageCount);
  }

  return pdf;
};
